import { useLocale } from "@/i18n/client";
import { useRouter } from "@/i18n/routing";
import { addLeadingSlash } from "@frend-digital/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useTransition } from "react";
import { useGlobal } from "../global/GlobalContext";
import { useAccountModal } from "./AccountModal";
import { ACCOUNT_PATHS } from "./config";

export const useSetAccountCampaign = () => {
	const { data: global } = useGlobal();
	const router = useRouter();
	const locale = useLocale();
	const queryClient = useQueryClient();

	return useCallback(
		async (beforeNavigate?: () => void) => {
			if (global.loginCampaignURI) {
				await fetch(addLeadingSlash(locale) + addLeadingSlash(global.loginCampaignURI));
				beforeNavigate?.();
				await sleep(100);
				window.location.reload();
			}
		},
		[global.loginCampaignURI, locale],
	);
};

/**
 * Custom hook to handle account navigation logic, with optional support for modal closure.
 *
 * @param {Object} options - Options object.
 * @param {boolean} [options.modal] - Determines if navigation should handle modal-specific behavior.
 * @param {boolean} [options.showAccountCreated] - Whether to show the account created modal after navigation.
 * @returns {[boolean, Function]} An array containing a boolean indicating if a transition is pending,
 * and a function to trigger the navigation.
 *
 * The hook creates a navigation function (`navigate`) that performs different actions
 * based on whether the `modal` option is set or not:
 * - If `modal` is `true`:
 *   - It checks for a `loginCampaignURI` in the global data. If present, it fetches this URL.
 *   - The router refreshes the current route.
 *   - After a short delay, it closes the account modal.
 * - If `modal` is `false`, it navigates to the root account path.
 */
export const useAccountNavigate = ({
	modal,
	showAccountCreated,
}: { modal?: boolean; showAccountCreated?: boolean }) => {
	const [isPending, startTransition] = useTransition();
	const router = useRouter();
	const [, setOpen] = useAccountModal();

	const activate = useSetAccountCampaign();

	const navigate = useCallback(() => {
		startTransition(async () => {
			if (modal) {
				await activate(() => {
					setOpen(false);
				});
				// Modal will show on current page after reload via sessionStorage
			} else {
				// Set sessionStorage flag before navigation
				if (showAccountCreated && typeof window !== "undefined") {
					sessionStorage.setItem("accountCreated", "true");
				}

				const path = showAccountCreated
					? `${ACCOUNT_PATHS.ROOT}?accountCreated=true`
					: ACCOUNT_PATHS.ROOT;
				router.push(path);
			}
		});
	}, [modal, router, setOpen, activate, showAccountCreated]);

	return [isPending, navigate] as const;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
